import { yupResolver } from '@hookform/resolvers/yup'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Box, Chip, FormControl, Grid2, IconButton, InputLabel, MenuItem, Paper, Select, Stack, Tooltip, Typography } from '@mui/material'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import * as Yup from 'yup'
import { Can } from '../../Components/Can'
import { ConfirmDialog } from '../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component'
import { Button } from '../../Components/Form/Button/button.scomponent'
import { Input } from '../../Components/Form/Input/input.component'
import { SelectComponent } from '../../Components/Form/Select/select.component'
import { Loader } from '../../Components/Loader'
import { Placeholder } from '../../Components/Placeholder/placeholder.component'
import { BaseContentPage } from '../../Components/Templates/BaseContentPage/base-content-page.componen'
import { BaseScreen } from '../../Components/Templates/BaseScreen/basescreen.component'
import { useToast } from '../../Hooks/ToasContext'
import {
  IProductMaskOptionEntity,
  TProductMaskOptionType,
  TProductMaskOptionsApiResponse,
} from '../../Services/api/product-mask-options/product-mask-options.interface'
import { CreateProductMaskOptionUseCase } from '../../Usecase/ProductMaskOptions/create-product-mask-option-usecase'
import { DestroyProductMaskOptionUseCase } from '../../Usecase/ProductMaskOptions/destroy-product-mask-option-usecase'
import { ListProductMaskOptionsUseCase } from '../../Usecase/ProductMaskOptions/list-product-mask-options-usecase'
import { UpdateProductMaskOptionUseCase } from '../../Usecase/ProductMaskOptions/update-product-mask-option-usecase'

const optionTypeLabels: Record<TProductMaskOptionType, string> = {
  type: 'Tipo de Produto',
  family: 'Família de Produto',
  volumetry: 'Volume',
  material: 'Matéria Prima Principal ou Versão para equipamentos',
  complement: 'Tipo de acabamento, personalização ou tipo de tampa plástica',
  version: 'Versão do produto',
  brand: 'Código do cliente',
}

type TGroupedMaskKey = 'types' | 'families' | 'volumetries' | 'materials' | 'complements' | 'versions' | 'brands'

const groupedKeys: Array<{ key: TGroupedMaskKey; type: TProductMaskOptionType }> = [
  { key: 'types', type: 'type' },
  { key: 'families', type: 'family' },
  { key: 'volumetries', type: 'volumetry' },
  { key: 'materials', type: 'material' },
  { key: 'complements', type: 'complement' },
  { key: 'versions', type: 'version' },
  { key: 'brands', type: 'brand' },
]

interface IProductMaskForm {
  type: TProductMaskOptionType | ''
  name: string
  value: string
}

interface IProductMasksProps {
  listProductMaskOptionsUseCase: ListProductMaskOptionsUseCase
  createProductMaskOptionUseCase: CreateProductMaskOptionUseCase
  updateProductMaskOptionUseCase: UpdateProductMaskOptionUseCase
  destroyProductMaskOptionUseCase: DestroyProductMaskOptionUseCase
}

const schema = Yup.object().shape({
  type: Yup.string().required('Tipo da opção é obrigatório'),
  name: Yup.string().required('Nome da opção é obrigatório'),
  value: Yup.string().required('Valor da opção é obrigatório'),
})

function normalizeOptions(response: TProductMaskOptionsApiResponse | null): IProductMaskOptionEntity[] {
  if (!response) return []

  if (Array.isArray(response)) {
    return response.map((option, index) => ({
      ...option,
      id: option.id || `${option.type}-${option.value}-${index}`,
      apiId: option.id,
      type: option.type || option.category || 'type',
      category: option.category || option.type,
      name: option.name || option.label || '',
      isActive: option.isActive ?? true,
    }))
  }

  return groupedKeys.flatMap(({ key, type }) => {
    const items = response[key] ?? []

    return items.map((option, index) => ({
      id: option.id || `${type}-${option.value}-${index}`,
      apiId: option.id,
      type,
      category: option.category as TProductMaskOptionType || type,
      name: option.name || option.label || '',
      label: option.label,
      value: option.value || '',
      isActive: option.isActive ?? true,
      sortOrder: option.sortOrder,
      createdAt: option.createdAt,
      updatedAt: option.updatedAt,
    }))
  })
}

const ProductMasks: FC<IProductMasksProps> = ({
  listProductMaskOptionsUseCase,
  createProductMaskOptionUseCase,
  updateProductMaskOptionUseCase,
  destroyProductMaskOptionUseCase,
}) => {
  const { showToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [options, setOptions] = useState<IProductMaskOptionEntity[]>([])
  const [editingOption, setEditingOption] = useState<IProductMaskOptionEntity | null>(null)
  const [optionToDestroy, setOptionToDestroy] = useState<IProductMaskOptionEntity | null>(null)
  const [optionToToggleStatus, setOptionToToggleStatus] = useState<IProductMaskOptionEntity | null>(null)
  const [preview, setPreview] = useState('')
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<TProductMaskOptionType | 'all'>('all')

  const { control, register, handleSubmit, formState, reset } = useForm<IProductMaskForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      type: '',
      name: '',
      value: '',
    },
  })

  const watchedType = useWatch({ control, name: 'type' })
  const watchedName = useWatch({ control, name: 'name' })
  const watchedValue = useWatch({ control, name: 'value' })
  const { errors } = formState

  const loadOptions = useCallback(async () => {
    try {
      setLoading(true)
      setLoadError(false)

      const result = await listProductMaskOptionsUseCase.handle({})

      if (result.isFailure) {
        showToast(String(result.error?.error ?? 'Erro ao carregar opções de máscara'), 'error')
        setLoadError(true)
        return
      }

      setOptions(normalizeOptions(result.getValue()))
    } catch (error: any) {
      setLoadError(true)
      showToast(error?.response?.data?.error ?? 'Erro ao carregar opções de máscara', 'error')
    } finally {
      setLoading(false)
    }
  }, [listProductMaskOptionsUseCase, showToast])

  const clearForm = () => {
    setEditingOption(null)
    reset({ type: '', name: '', value: '' })
    setPreview('')
  }

  const handleEdit = (option: IProductMaskOptionEntity) => {
    if (!option.apiId) {
      showToast('Esta opção não possui identificador para edição.', 'error')
      return
    }

    setEditingOption(option)
    reset({
      type: option.type,
      name: option.name || option.label || '',
      value: option.value,
    })
  }

  const handleSave: SubmitHandler<IProductMaskForm> = async values => {
    if (!values.type) return

    try {
      setLoading(true)

      const payload = {
        type: values.type,
        category: values.type,
        name: values.name.trim(),
        value: values.value.trim(),
      }

      const result = editingOption?.apiId
        ? await updateProductMaskOptionUseCase.handle({ ...payload, id: editingOption.apiId })
        : await createProductMaskOptionUseCase.handle(payload)

      if (result.isFailure) {
        showToast(String(result.error?.error ?? 'Erro ao salvar opção'), 'error')
        return
      }

      showToast(editingOption ? 'Opção atualizada' : 'Opção cadastrada', 'success')
      clearForm()
      await loadOptions()
    } catch (error: any) {
      showToast(error?.response?.data?.error ?? 'Erro ao salvar opção', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async () => {
    if (!optionToToggleStatus?.apiId) return

    const nextIsActive = optionToToggleStatus.isActive === false

    try {
      setLoading(true)
      const result = await updateProductMaskOptionUseCase.handle({
        id: optionToToggleStatus.apiId,
        isActive: nextIsActive,
      })

      if (result.isFailure) {
        showToast(String(result.error?.error ?? 'Erro ao alterar status da opção'), 'error')
        return
      }

      showToast(nextIsActive ? 'Opção ativada' : 'Opção desativada', 'success')
      setOptionToToggleStatus(null)
      await loadOptions()
    } finally {
      setLoading(false)
    }
  }

  const handleDestroy = async () => {
    if (!optionToDestroy?.apiId) return

    try {
      setLoading(true)
      const result = await destroyProductMaskOptionUseCase.handle(optionToDestroy.apiId)

      if (result.isFailure) {
        showToast(String(result.error?.error ?? 'Erro ao excluir opção'), 'error')
        return
      }

      showToast('Opção excluída', 'success')
      setOptionToDestroy(null)
      await loadOptions()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOptions()
  }, [loadOptions])

  useEffect(() => {
    setPreview(watchedValue || '')
  }, [watchedValue])

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'type',
      headerName: 'Tipo de Opção',
      minWidth: 220,
      flex: 1,
      renderCell: (params: any) => optionTypeLabels[params.value as TProductMaskOptionType] || params.value,
    },
    {
      field: 'name',
      headerName: 'Nome da Opção',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'value',
      headerName: 'Valor',
      minWidth: 120,
      flex: 0.6,
      renderCell: (params: any) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'sortOrder',
      headerName: 'Ordem',
      width: 90,
      renderCell: (params: any) => params.value ?? '-',
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params: any) => (
        <Chip
          label={params.value === false ? 'Inativa' : 'Ativa'}
          size="small"
          color={params.value === false ? 'default' : 'success'}
          variant={params.value === false ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 140,
      renderCell: (params: any) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Editar">
            <span>
              <IconButton size="small" onClick={() => handleEdit(params.row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={params.row.isActive === false ? 'Ativar' : 'Desativar'}>
            <span>
              <IconButton
                size="small"
                disabled={!params.row.apiId}
                onClick={() => setOptionToToggleStatus(params.row)}
              >
                {params.row.isActive === false ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Excluir">
            <span>
              <IconButton size="small" color="error" disabled={!params.row.apiId} onClick={() => setOptionToDestroy(params.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ], [])

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()

    const optionsByType = typeFilter === 'all'
      ? options
      : options.filter(option => option.type === typeFilter || option.category === typeFilter)

    if (!normalizedSearch) return optionsByType

    return optionsByType.filter(option => {
      const typeLabel = optionTypeLabels[option.type] || option.type
      const status = option.isActive === false ? 'inativa' : 'ativa'

      return [
        typeLabel,
        option.type,
        option.category,
        option.name,
        option.label,
        option.value,
        status,
      ].some(value => String(value || '').toLowerCase().includes(normalizedSearch))
    })
  }, [options, search, typeFilter])

  return (
    <Can permissions={['menu:productMasks:manage']}>
      <BaseScreen>
        <ConfirmDialog
          title={optionToToggleStatus?.isActive === false ? 'Ativar opção' : 'Desativar opção'}
          description={`Tem certeza que deseja ${optionToToggleStatus?.isActive === false ? 'ativar' : 'desativar'} "${optionToToggleStatus?.name}"?`}
          open={!!optionToToggleStatus}
          onCancel={() => setOptionToToggleStatus(null)}
          onConfirm={handleToggleStatus}
          confirmButtonTitle={optionToToggleStatus?.isActive === false ? 'Ativar' : 'Desativar'}
        />
        <ConfirmDialog
          title="Excluir opção"
          description={`Tem certeza que deseja excluir "${optionToDestroy?.name}"?`}
          open={!!optionToDestroy}
          onCancel={() => setOptionToDestroy(null)}
          onConfirm={handleDestroy}
          confirmButtonTitle="Excluir"
        />
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
          <BaseContentPage
            title="Cadastro de Máscaras"
            subtitle="Gerencie as opções usadas para montar a máscara única do produto"
            breadcrumb={[{ title: 'Cadastro de Máscaras', link: '/product-masks' }]}
            minHeight="auto"
          >
            {loading && <Loader />}

            <Box component="form" onSubmit={handleSubmit(handleSave)} sx={{ mb: 4 }}>
              <Grid2 container spacing={2} alignItems="flex-start">
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <SelectComponent
                    label="Tipo de Opção"
                    placeholder=""
                    name="type"
                    control={control}
                    error={!!errors.type}
                    helperText={errors.type?.message}
                    options={Object.entries(optionTypeLabels).map(([value, label]) => ({ value, label }))}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <Input
                    label="Nome da Opção"
                    placeholder="Digite o nome"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    {...register('name')}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 2 }}>
                  <Input
                    label="Valor da opção"
                    placeholder="Digite o valor"
                    error={!!errors.value}
                    helperText={errors.value?.message}
                    {...register('value')}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 2 }}>
                  <Stack spacing={1}>
                    <Button type="submit" isLoading={loading}>
                      {editingOption ? 'Atualizar' : 'Adicionar'}
                    </Button>
                    {editingOption && (
                      <Button type="button" variant="outlined" onClick={clearForm}>
                        Cancelar
                      </Button>
                    )}
                  </Stack>
                </Grid2>
              </Grid2>

              <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Preview da máscara
                    </Typography>
                    <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                      {preview || watchedValue || 'Preencha tipo e valor para visualizar'}
                    </Typography>
                    {watchedType && watchedName && (
                      <Typography variant="body2" color="text.secondary">
                        {optionTypeLabels[watchedType as TProductMaskOptionType]}: {watchedName}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Box>

            {!!options.length ? (
              <Box sx={{ width: '100%' }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  justifyContent="space-between"
                  sx={{ mb: 1.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {filteredOptions.length} de {options.length} opções
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
                    <FormControl sx={{ minWidth: { xs: '100%', md: 260 } }}>
                      <InputLabel id="product-mask-type-filter-label">Tipo de Opção</InputLabel>
                      <Select
                        labelId="product-mask-type-filter-label"
                        label="Tipo de Opção"
                        value={typeFilter}
                        onChange={(event) => setTypeFilter(event.target.value as TProductMaskOptionType | 'all')}
                      >
                        <MenuItem value="all">Todos os tipos</MenuItem>
                        {Object.entries(optionTypeLabels).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Input
                      label="Pesquisar"
                      placeholder="Buscar por tipo, nome, valor ou status"
                      value={search}
                      onChange={(event: any) => setSearch(event.target.value)}
                      sx={{ width: { xs: '100%', md: 380 } }}
                    />
                  </Stack>
                </Stack>
                <Box sx={{ width: '100%', height: 'calc(100vh - 460px)', minHeight: 420 }}>
                  <DataGrid
                    rows={filteredOptions}
                    columns={columns}
                    rowHeight={52}
                    disableRowSelectionOnClick
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 25 },
                      },
                    }}
                    sx={{
                      border: 'none',
                      bgcolor: 'background.paper',
                      '& .MuiDataGrid-columnHeaders': {
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                      '& .MuiDataGrid-cell': {
                        border: 'none',
                      },
                      '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                    }}
                  />
                </Box>
              </Box>
            ) : loadError ? (
              <Placeholder description="Não foi possível carregar as opções de máscara." onRetry={loadOptions} />
            ) : (
              <Placeholder description="Nenhuma opção de máscara encontrada." />
            )}
          </BaseContentPage>
        </Paper>
      </BaseScreen>
    </Can>
  )
}

export { ProductMasks }
